'use client'

import { useEffect, useState } from 'react'
import { I18nextProvider } from 'react-i18next'
import i18n from 'i18next'
import { initReactI18next } from 'react-i18next'
import LanguageDetector from 'i18next-browser-languagedetector'

interface I18nProviderProps {
  children: React.ReactNode
}

export default function I18nProvider({ children }: I18nProviderProps) {
  const [isInitialized, setIsInitialized] = useState(false)

  useEffect(() => {
    const initializeI18n = async () => {
      if (i18n.isInitialized) {
        setIsInitialized(true)
        return
      }

      // Set a timeout to prevent infinite loading
      const timeout = setTimeout(() => {
        console.warn('i18n initialization timeout, proceeding with fallback')
        setIsInitialized(true)
      }, 5000)

      try {
        // Load translations dynamically with better error handling
        const loadTranslation = async (lang: string) => {
          try {
            const response = await fetch(`/locales/${lang}.json`)
            if (!response.ok) {
              console.warn(`Failed to load ${lang}.json: ${response.status}`)
              return null
            }
            return await response.json()
          } catch (error) {
            console.warn(`Error loading ${lang}.json:`, error)
            return null
          }
        }

        const [enTranslations, esTranslations, frTranslations, svTranslations, deTranslations, idTranslations, zhCNTranslations, koTranslations, ruTranslations] = await Promise.all([
          loadTranslation('en'),
          loadTranslation('es'),
          loadTranslation('fr'),
          loadTranslation('sv'),
          loadTranslation('de'),
          loadTranslation('id'),
          loadTranslation('zh-CN'),
          loadTranslation('ko'),
          loadTranslation('ru')
        ])

        type TranslationResource = Record<string, unknown>
        const resources: Record<string, { translation: TranslationResource }> = {}
        if (enTranslations) resources.en = { translation: enTranslations }
        if (esTranslations) resources.es = { translation: esTranslations }
        if (frTranslations) resources.fr = { translation: frTranslations }
        if (svTranslations) resources.sv = { translation: svTranslations }
        if (deTranslations) resources.de = { translation: deTranslations }
        if (idTranslations) resources.id = { translation: idTranslations }
        if (zhCNTranslations) {
          resources['zh-CN'] = { translation: zhCNTranslations }
          // Alias plain 'zh' to zh-CN to handle detectors that return 'zh'
          resources['zh'] = { translation: zhCNTranslations }
        }
        if (koTranslations) resources.ko = { translation: koTranslations }
        if (ruTranslations) resources.ru = { translation: ruTranslations }

        // Ensure we have at least English
        if (!resources.en) {
          throw new Error('Failed to load English translations')
        }

        await i18n
          .use(LanguageDetector)
          .use(initReactI18next)
          .init({
            resources,
            fallbackLng: 'en',
            supportedLngs: ['en', 'es', 'fr', 'sv', 'de', 'id', 'zh-CN', 'zh', 'ko', 'ru'],
            debug: false,
            interpolation: {
              escapeValue: false,
            },
            detection: {
              order: ['localStorage', 'navigator', 'htmlTag'],
              caches: ['localStorage'],
            }
          })

        clearTimeout(timeout)
        setIsInitialized(true)
      } catch (error) {
        console.error('Failed to initialize i18n:', error)
        // Try to initialize with just English as fallback
        try {
          const enTranslations = await fetch('/locales/en.json').then(r => r.json())
          await i18n
            .use(LanguageDetector)
            .use(initReactI18next)
            .init({
              resources: {
                en: { translation: enTranslations }
              },
              fallbackLng: 'en',
              debug: true,
              interpolation: {
                escapeValue: false,
              }
            })
          clearTimeout(timeout)
          setIsInitialized(true)
        } catch (fallbackError) {
          clearTimeout(timeout)
          console.error('Failed to initialize i18n even with fallback:', fallbackError)
          setIsInitialized(true) // Still render children even if i18n fails
        }
      }
    }

    initializeI18n()
  }, [])

  if (!isInitialized) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-emerald-600 mx-auto mb-4"></div>
          <p className="text-slate-600">Loading translations...</p>
        </div>
      </div>
    )
  }

  return (
    <I18nextProvider i18n={i18n}>
      {children}
    </I18nextProvider>
  )
}
